import { ConsentFormDefinition } from '../types';

export const consentSpecifiedDocument: ConsentFormDefinition = {
  formKey: 'consent-specified-document',
  title: 'Consent to Specified Document',
  pdfTitle: 'Consent to Additional Terms or Agreement',
  intro:
    'This form is used to obtain your consent to specific additional terms, agreements, or policies related to your behavioral health treatment through {providerName}. Please read the attached document carefully before signing.',
  requiresProviderInput: [
    {
      type: 'text',
      id: 'documentTitle',
      label: 'Document Title / Name',
      placeholder: 'e.g., "Payment Plan Agreement," "Group Therapy Rules," "SUD Treatment Addendum"',
      required: true,
    },
    { type: 'url', id: 'docLink', label: 'Link to Document (if applicable)' },
    {
      type: 'text',
      id: 'documentVersion',
      label: 'Version/Ref # (optional)',
      placeholder: 'e.g., "v1.2," "Revision 2024"',
      halfWidth: true,
    },
    {
      type: 'text',
      id: 'documentPages',
      label: 'Pages # (optional)',
      placeholder: 'e.g., 1, 2, 3',
      halfWidth: true,
    },
  ],
  sections: [
    {
      id: 'patient',
      navLabel: 'Patient Info',
      heading: 'Your Information',
      blocks: [
        {
          type: 'field-group',
          layout: '2-1',
          fields: [
            {
              type: 'text',
              id: 'firstName',
              label: 'First Name',
              placeholder: 'First name',
              required: true,
            },
            {
              type: 'text',
              id: 'middleInitial',
              label: 'Middle Initial (optional)',
              placeholder: 'M.I.',
            },
            {
              type: 'text',
              id: 'lastName',
              label: 'Last Name',
              placeholder: 'Last name',
              required: true,
            },
            {
              type: 'date',
              id: 'dob',
              label: 'Date of Birth',
              required: true,
            },
          ],
        },
      ],
    },
    {
      id: 'document-type',
      navLabel: 'Document Type',
      heading: 'What Are You Consenting To?',
      blocks: [
        {
          type: 'paragraph',
          text: 'This form is for consent to additional agreements. Common examples include:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'Payment plans or financial agreements',
            'Group therapy rules and confidentiality agreements',
            'Substance use disorder (SUD) treatment specific terms',
            'Intensive outpatient program (IOP) or partial hospitalization (PHP) agreements',
            'Telehealth platform terms and technical requirements',
            'Supervision or consultation policies',
            'Recording or photography release forms (if applicable)',
            'Conflict of interest acknowledgments',
            'Release of information authorizations',
            'Other practice-specific policies or procedures',
          ],
        },
        {
          type: 'paragraph',
          text: 'The specific document you are consenting to is attached or referenced below. Please make sure you have read it before signing this form.',
          bold: true,
        },
      ],
    },
    {
      id: 'document-details',
      navLabel: 'Document Details',
      heading: 'Identification of Document',
      blocks: [
        {
          type: 'field-group',
          layout: '2-1',
          pdfTable: true,
          pdfTableWidth: 0.6,
          fields: [
            {
              type: 'text',
              id: 'documentTitle',
              label: 'Title of Document',
              placeholder: 'e.g., "Payment Plan Agreement," "Group Therapy Rules," "SUD Treatment Addendum"',
              required: true,
            },
            {
              type: 'url',
              id: 'docLink',
              label: 'Link to Document (if applicable)',
              hideIfEmpty: true,
            },
            {
              type: 'text',
              id: 'documentVersion',
              label: 'Version/Ref # (optional)',
              placeholder: 'e.g., "v1.2," "Revision 2024"',
              hideIfEmpty: true,
            },
            {
              type: 'text',
              id: 'documentPages',
              label: 'Pages # (optional)',
              placeholder: 'e.g., 1, 2, 3',
              hideIfEmpty: true,
            },
          ],
        },
      ],
    },
    {
      id: 'purpose',
      navLabel: 'Purpose',
      heading: 'Why This Agreement Is Needed',
      blocks: [
        {
          type: 'paragraph',
          text: 'This document outlines additional terms related to your behavioral health treatment. It may include:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'Specific policies or procedures for your treatment setting (e.g., group therapy confidentiality rules, medication management protocols)',
            'Financial arrangements or payment terms',
            'Specialized treatment program requirements (e.g., substance use disorder, intensive outpatient)',
            'Technology or platform-specific guidelines (e.g., telehealth recording policies, session recording)',
            'Release of information to other providers or insurance companies',
            'Acknowledgment of specific risks or benefits relevant to your treatment type',
            'Practice-specific policies (e.g., cancellation fees, emergency contact protocols)',
            'Conflict of interest or dual relationship acknowledgments',
          ],
        },
        {
          type: 'paragraph',
          text: 'This agreement is intended to clarify terms and expectations between you and your provider.',
        },
      ],
    },
    {
      id: 'understanding',
      navLabel: 'Your Understanding',
      heading: 'Do You Understand and Agree?',
      blocks: [
        {
          type: 'paragraph',
          text: 'Before signing, please confirm:',
          bold: true,
        },
        {
          type: 'checklist',
          id: 'understanding-checklist',
          options: [
            { id: 'understand-read', label: 'I have read and understand the attached or referenced document' },
            {
              id: 'understand-terms',
              label: 'I understand the key terms, conditions, and expectations outlined in the document',
            },
            { id: 'understand-voluntary', label: 'I am agreeing to this document voluntarily and of my own free will' },
            {
              id: 'understand-questions',
              label: 'I have had the opportunity to ask questions and have received answers',
            },
            {
              id: 'understand-agree',
              label: 'I agree to the terms, conditions, and requirements outlined in the document',
            },
          ],
        },
      ],
    },
    {
      id: 'questions',
      navLabel: 'Questions?',
      heading: 'Questions About This Document?',
      blocks: [
        {
          type: 'paragraph',
          text: 'If you do not understand any part of this document, or if you have questions or concerns:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'Ask your therapist or provider for clarification',
            'Request a copy of the document to review at home',
            "Ask for a written explanation of any terms you don't understand",
            'Request a reasonable delay in signing if you need more time to think',
            "Do not sign if you don't agree or understand",
          ],
        },
        {
          type: 'paragraph',
          text: 'You have the right to understand everything you are agreeing to. A good provider will welcome your questions.',
        },
      ],
    },
    {
      id: 'right-to-refuse',
      navLabel: 'Your Rights',
      heading: 'You Have Rights',
      blocks: [
        {
          type: 'bullet-list',
          items: [
            'Right to ask questions before signing',
            'Right to request clarification of any terms',
            'Right to take time to review before signing',
            'Right to decline to sign if you disagree',
            'Right to discuss concerns with your provider',
            'Right to request modifications to the agreement (provider may or may not agree)',
            'Right to understand how this agreement affects your treatment',
            'Right to a copy of the signed agreement',
          ],
        },
      ],
    },
    {
      id: 'revocation',
      navLabel: 'Revoking Consent',
      heading: 'Can You Change Your Mind?',
      blocks: [
        {
          type: 'paragraph',
          text: 'Whether you can revoke or change this agreement depends on the specific document:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'Some agreements (e.g., financial terms, policy acknowledgments) can be modified or terminated with written notice',
            'Some agreements (e.g., release of information) may allow revocation but not retroactively',
            'Some agreements may be binding for a specific period',
            'Your provider will explain what changes are possible',
          ],
        },
        {
          type: 'paragraph',
          text: 'Ask your provider: "Can I revoke or modify this agreement later? If so, how?"',
        },
      ],
    },
    {
      id: 'signature-explanation',
      navLabel: 'About Your Signature',
      heading: 'What Your Signature Means',
      blocks: [
        {
          type: 'paragraph',
          text: 'By signing this form, you are confirming:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'You have received a copy of the document you are consenting to (or a summary of it)',
            'You have read and understood the document',
            'You understand the terms, conditions, and any requirements or expectations',
            'You are agreeing to the terms outlined in the document',
            'You are signing of your own free will, without pressure or coercion',
            'You understand your rights regarding this agreement',
          ],
        },
        {
          type: 'paragraph',
          text: 'Your signature is a legal acknowledgment of your understanding and agreement.',
        },
      ],
    },
    {
      id: 'before-sign',
      navLabel: 'Before You Sign',
      heading: 'Checklist Before Signing',
      blocks: [
        {
          type: 'paragraph',
          text: 'Before you sign, make sure:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'You have read the entire document',
            'You understand what you are agreeing to',
            'You have asked any questions you have',
            'You have received clear answers',
            'You are voluntarily choosing to sign (not under pressure)',
            'You understand the terms apply to you and/or your child',
            'You know who to contact if you have future questions or concerns',
            'You have been offered a copy to keep',
          ],
        },
        {
          type: 'paragraph',
          text: 'Do NOT sign this form unless you have checked all items above and feel comfortable with the agreement.',
        },
      ],
    },
    {
      id: 'sign',
      navLabel: 'Signature',
      heading: 'Your Signature',
      blocks: [
        {
          type: 'field-group',
          fields: [
            {
              type: 'text',
              id: 'printedName',
              label: 'Your Name (print)',
              placeholder: 'Print your name',
              required: true,
              fullWidth: true,
            },
            {
              type: 'select',
              id: 'relationship',
              label: 'Signing as:',
              options: [
                { value: 'self', label: 'Myself (the patient)' },
                { value: 'parent', label: 'Parent of patient' },
                { value: 'guardian', label: 'Legal Guardian' },
                { value: 'poa', label: 'Power of Attorney' },
              ],
            },
          ],
        },
        {
          type: 'paragraph',
          text: 'By signing below, I confirm that I have read, understood, and agree to the terms outlined in the attached or referenced document.',
        },
        { type: 'signature' },
      ],
    },
  ],
  requiredFields: [
    'firstName',
    'lastName',
    'dob',
    'documentTitle',
    'understanding-checklist:understand-read',
    'understanding-checklist:understand-terms',
    'understanding-checklist:understand-voluntary',
    'understanding-checklist:understand-questions',
    'understanding-checklist:understand-agree',
    'signature',
    'printedName',
  ],
};
